import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import '../App.css';

const topics = [
  {
    title: 'Narcissism',
    description:
      'Narcissistic traits, Narcissistic Personality Disorder, grandiose and vulnerable presentations, empathy, and interpersonal functioning.',
    path: '/topics/narcissism',
  },
  {
    title: 'Gaslighting',
    description:
      'How repeated reality distortion can undermine confidence in memory, perception, judgment, and personal agency.',
    path: '/topics/gaslighting',
  },
  {
    title: 'Triangulation',
    description:
      'Three-person relationship dynamics, alliance-building, jealousy induction, indirect communication, and manipulation.',
    path: '/topics/triangulation',
  },
  {
    title: 'Trauma Bonding',
    description:
      'Attachment within abusive or coercive relationships, including power imbalance, intermittent mistreatment, and barriers to leaving.',
    path: '/topics/trauma-bonding',
  },
  {
    title: 'Emotional Coercion',
    description:
      'The use of guilt, fear, shame, obligation, withdrawal, and psychological pressure to influence another person’s choices.',
    path: '/topics/emotional-coercion',
  },
  {
    title: 'Hoovering',
    description:
      'A non-clinical term for attempts to re-establish contact after separation, examined alongside coercive control and post-separation abuse.',
    path: '/topics/hoovering',
  },
  {
    title: 'Narcissism, Sex & Intimacy',
    description:
      'Research on sexual narcissism, entitlement, empathy, consent, coercion, and the dynamics of intimate relationships.',
    path: '/topics/sex-and-narcissism',
  },
  {
    title: 'Limerence',
    description:
      'Intense relational preoccupation, intrusive thinking, longing for reciprocation, uncertainty, attachment, and impairment.',
    path: '/topics/limerence',
  },
];

const Topics = () => {
  return (
    <>
      <Helmet>
        <title>
          Psychological Manipulation Topics | Behavior, Power & Control
        </title>

        <meta
          name="description"
          content="Explore evidence-informed articles on narcissism, gaslighting, triangulation, trauma bonding, emotional coercion, limerence, intimacy, and psychological control."
        />
      </Helmet>

      <main className="topic-page">
        <header>
          <p className="articleEyebrow">
            Concepts · Research · Human Behavior
          </p>

          <h1>Explore Psychological Topics</h1>

          <p className="articleLead">
            Psychological manipulation encompasses a range of behaviors and
            relationship dynamics involving influence, power, coercion,
            perception, attachment, and autonomy.
          </p>
        </header>

        <section className="researchNote">
          <strong>How to use this resource:</strong> The articles distinguish
          observable behavior from diagnosis and identify when terminology is
          clinical, research-based, emerging, or primarily used in popular
          relationship literature. Individual behaviors should always be
          interpreted within their broader context.
        </section>

        <section>
          <h2>Core Topics</h2>

          <p>
            Each topic examines a different aspect of psychological
            manipulation or interpersonal behavior. Many overlap, but they
            should not be treated as interchangeable concepts.
          </p>

          <div className="topicDirectory">
            {topics.map((topic) => (
              <Link
                key={topic.path}
                to={topic.path}
                className="topicDirectoryCard"
              >
                <h3>{topic.title}</h3>

                <p>{topic.description}</p>

                <span className="topicDirectoryLink">
                  Explore topic →
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section>
          <h2>Related Areas of Study</h2>

          <p>
            These subjects intersect with several broader areas of psychology
            and sociology, including:
          </p>

          <ul className="scholarlyList">
            <li>Coercive control</li>
            <li>Psychological and emotional abuse</li>
            <li>Intimate partner violence</li>
            <li>Personality and personality disorders</li>
            <li>Attachment and relationship functioning</li>
            <li>Social influence and persuasion</li>
            <li>Family systems</li>
            <li>Power and dependency</li>
            <li>Jealousy and mate-retention behavior</li>
            <li>Ostracism and social exclusion</li>
            <li>Sexual coercion and consent</li>
            <li>Stalking and post-separation abuse</li>
          </ul>
        </section>

        <section>
          <h2>Behavior Before Diagnosis</h2>

          <p>
            One of the most important distinctions throughout this site is the
            difference between describing behavior and diagnosing a person.
          </p>

          <p>
            Terms such as gaslighting, intimidation, sexual coercion,
            surveillance, isolation, humiliation, or repeated boundary
            violations describe behaviors that can be evaluated without knowing
            whether the person responsible has a mental health diagnosis.
          </p>

          <p>
            Personality disorders require professional assessment. Observable
            harmful behavior does not.
          </p>
        </section>

        <section className="researchNote">
          <strong>Editorial standard:</strong> Where evidence is established,
          the articles aim to represent it accurately. Where concepts are
          debated, informal, or still developing, those limitations are stated
          rather than presented as settled psychological fact.
        </section>

        <section className="educationalDisclaimer">
          <strong>Educational scope:</strong> The material on this site is
          intended for education and research-oriented discussion. It does not
          provide diagnosis, psychotherapy, legal advice, or individualized
          assessment of any relationship.
        </section>
      </main>
    </>
  );
};

export default Topics;